/**
 * useMentalityContext — owns the fetch + cancel lifecycle for the
 * mentality overlay.
 *
 * - Re-fetches whenever the panel is open and the module filter
 *   changes, OR when the focus entity (e.g. focused mentee) changes.
 *   Focus is part of the dep set so situational ranking updates the
 *   moment the user pins a different mentee — the cards re-rank to
 *   "this mentee" without a manual reload.
 * - When `focus` is null the call falls back to the global ranking
 *   (no focus id forwarded).
 * - Cancel-via-ref prevents stale payloads from overwriting a newly-set
 *   module/focus, and silences the "set state after unmount" warning
 *   when the overlay closes mid-fetch.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
    getMentalityContext, type MentalityContext, type ModuleKey,
} from '../../../mentorshipService';
import { captureError } from '../../../core/errors/captureError';
import { GLOSSARY } from '../glossary';
import type { FocusEntity } from '../focus/useFocusContext';

export function useMentalityContext(
    open: boolean,
    module: ModuleKey | '',
    focus: FocusEntity | null = null,
) {
    const [context, setContext] = useState<MentalityContext | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const cancelRef = useRef(false);

    // The fetch only cares about the focused entity's stable id, not the
    // rest of the entity payload. Threading just the id keeps the
    // useCallback dep set narrow so we don't refetch on every label edit
    // (only on actual focus changes).
    const focusId = focus?.id ?? null;

    const fetchContext = useCallback(async () => {
        setLoading(true);
        setError(null);
        cancelRef.current = false;
        try {
            const data = await getMentalityContext(
                module || undefined,
                focusId ?? undefined,
            );
            if (cancelRef.current) return;
            setContext(data);
        } catch (err) {
            captureError(err, {
                scope: 'MentalityPanel',
                op: 'getMentalityContext',
                extra: { module: module || null, focusId },
            });
            if (!cancelRef.current) {
                setError(
                    `Could not load your ${GLOSSARY.MENTALITY.shortLabel.toLowerCase()} overlay.`,
                );
            }
        } finally {
            if (!cancelRef.current) setLoading(false);
        }
    }, [module, focusId]);

    useEffect(() => {
        if (!open) return;
        void fetchContext();
        return () => { cancelRef.current = true; };
    }, [open, fetchContext]);

    return { context, loading, error, fetchContext, cancelRef };
}
